/**
 * SSO 单点登录工具
 *
 * 支持两种跳转模式，通过各子应用的 ssoMode 配置决定：
 *
 * - "shared"：同域共享 cookie，直接跳转。
 *   适用于子应用与门户同属一个顶级域（如 .oran.cn）的场景，
 *   登录 token 通过 cookie（domain=.oran.cn）自动共享，无需额外认证。
 *
 * - "oauth2"：走 OAuth2 Authorization Code 流程。
 *   适用于跨顶级域的场景，cookie 无法跨域共享，需要先获取 authorization code 再携带跳转。
 *   流程：
 *   1. 前端调用 GET /oauth2/authorize?clientId=<子应用clientId>（携带 Bearer token）
 *   2. 后端验证通过后返回 authorization code
 *   3. 前端携带 code 跳转到子应用首页（?oauth_code=xxx）
 *   4. 子应用后端用 code 调用 POST /oauth2/token 换取 access token，完成 SSO 登录
 *
 * 子应用注册表见 config.subApps，通过 ?login=<appKey> 触发对应的 SSO 跳转。
 */

import { oauth2Authorize } from "@/lib/api/auth";
import { config } from "@/lib/config";
import { logger } from "@/lib/logger";
import { getToken } from "./auth-storage";

/**
 * 跳转到指定子应用
 *
 * 根据 config.subApps[appKey].ssoMode 选择跳转方式：
 * - "shared"  → 同域 cookie 共享，同步直接跳转
 * - "oauth2"  → 先获取 authorization code，再携带 code 异步跳转
 *
 * 内部已处理 API 层面的降级（接口失败/未返回 code 时降级为直接跳转），
 * 但未登录或 appKey 未注册时会抛出异常，调用方需按场景处理。
 *
 * @param appKey 子应用标识，需在 config.subApps 中注册（如 "toolbox"、"prompt"）
 * @throws NOT_AUTHENTICATED 未登录
 * @throws Unknown sub-app: xxx  appKey 未在 config.subApps 中注册
 */
export async function navigateToSubApp(appKey: string): Promise<void> {
  const token = getToken();
  if (!token) {
    throw new Error("NOT_AUTHENTICATED");
  }

  const app = config.subApps[appKey];
  // hasOwnProperty 校验已在 isSubAppKey 中完成，但此处仍需防御性检查，
  // 防止直接调用 navigateToSubApp 时传入 "toString" 等原型属性拿到函数体
  if (!app || typeof app !== "object" || !app.baseUrl) {
    throw new Error(`Unknown sub-app: ${appKey}`);
  }

  if (app.ssoMode === "oauth2") {
    try {
      const res = await oauth2Authorize(app.clientId);

      if (res.success && res.data?.code) {
        // 拿到 authorization code，以 oauth_code 查询参数拼接到子应用首页地址
        const callbackUrl = `${app.baseUrl}?oauth_code=${encodeURIComponent(res.data.code)}`;
        logger.info(`SSO (oauth2): redirecting to ${appKey} with oauth_code`);
        window.location.href = callbackUrl;
        return;
      }
      // 后端未返回 code（可能用户未授权或会话过期），降级为直接跳转
      logger.warn(
        `SSO (oauth2): oauth2/authorize did not return code for ${appKey}, falling back to direct navigation`
      );
      // 统一出口：shared 模式直接跳
      window.location.href = app.baseUrl;
    } catch (error) {
      // 401 已被 handleHttpResponse 统一处理（clearAuth + throw），此时 token 已清除
      // token 没了 → 跳登录页；token 还在 → 网络/500 等临时错误，降级直接跳
      if (!getToken()) {
        logger.warn(`SSO (oauth2): token expired for ${appKey}, redirecting to login`);
        window.location.href = `/?login=${appKey}`;
      } else {
        logger.error(`SSO (oauth2): failed for ${appKey}, falling back to direct navigation`, error as Error);
        window.location.href = app.baseUrl;
      }
    }
  } else {
    logger.info(`SSO (shared): redirecting to ${appKey} via cookie sharing`);
    // 统一出口：shared 模式直接跳
    window.location.href = app.baseUrl;
  }
}

/**
 * 判断指定子应用的 SSO 模式是否为异步（oauth2）
 * 调用方据此决定是否需要 loading 状态
 *
 * @param appKey 子应用标识，需在 config.subApps 中注册
 */
export function isSSOAsync(appKey: string): boolean {
  return config.subApps[appKey]?.ssoMode === "oauth2";
}
